import { useState } from 'react'
import { formatPrice } from '../../lib/format'
import { Button, Chip, Dialog, Input } from '../ui'
import { parseDollarsToCents } from './money'

/** Client mirror of the server top-up bounds (Wallet::Config is authoritative). */
export const TOP_UP_MIN_CENTS = 500
export const TOP_UP_MAX_CENTS = 10_000

const PRESET_CENTS = [500, 1000, 2000]

const usd = (cents: number) => formatPrice({ cents, currency: 'USD' })

interface TopUpDialogProps {
  open: boolean
  onClose: () => void
  /**
   * Phase 1 → 2 boundary: creates the PaymentIntent server-side
   * (POST /wallet/top_ups) and resolves the Elements client secret.
   */
  onCreateIntent: (amountCents: number) => Promise<{ clientSecret: string }>
  /**
   * Phase-2 slot — render the payment form here (TopUpPaymentForm owns the
   * Stripe Elements wrapper; this dialog never imports @stripe/*).
   */
  children: (ctx: { clientSecret: string; amountCents: number; onBack: () => void }) => React.ReactNode
}

/**
 * Two-phase top-up dialog. Phase 1 picks an amount (presets + custom, client
 * mirror of the 500–10 000¢ server bounds); phase 2 hands the resolved client
 * secret to the payment-form slot. All money truth stays server-side.
 */
export function TopUpDialog({ open, onClose, onCreateIntent, children }: TopUpDialogProps) {
  const [presetCents, setPresetCents] = useState<number>(1000)
  const [custom, setCustom] = useState('')
  // Phase 2 = a confirmed amount + client secret snapshot; null = phase 1.
  const [intent, setIntent] = useState<{ clientSecret: string; amountCents: number } | null>(null)
  const [busy, setBusy] = useState(false)
  const [intentError, setIntentError] = useState('')

  const amountCents = custom !== '' ? parseDollarsToCents(custom) : presetCents
  const outOfBounds =
    amountCents !== null && (amountCents < TOP_UP_MIN_CENTS || amountCents > TOP_UP_MAX_CENTS)
  const amountError =
    custom !== '' && amountCents === null
      ? 'Enter an amount in dollars, like 12.50.'
      : outOfBounds
        ? `Top-ups are between ${usd(TOP_UP_MIN_CENTS)} and ${usd(TOP_UP_MAX_CENTS)}.`
        : ''

  const handleClose = () => {
    setIntent(null)
    setBusy(false)
    setIntentError('')
    onClose()
  }

  const handleContinue = async () => {
    if (amountCents === null || outOfBounds || busy) return
    setBusy(true)
    setIntentError('')
    try {
      const { clientSecret } = await onCreateIntent(amountCents)
      setIntent({ clientSecret, amountCents })
    } catch {
      setIntentError('Couldn’t start this top-up — try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} title="Add funds">
      {intent === null ? (
        <div className="space-y-4">
          <p className="text-sm text-text-muted">
            Top up your Fireside wallet. Money only leaves it when you chip in on a group gift.
          </p>
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Amount">
            {PRESET_CENTS.map((cents) => (
              <Chip
                key={cents}
                mode="check"
                selected={custom === '' && presetCents === cents}
                onToggle={() => {
                  setPresetCents(cents)
                  setCustom('')
                }}
              >
                {usd(cents)}
              </Chip>
            ))}
          </div>
          <Input
            label="Custom amount"
            inputMode="decimal"
            placeholder="12.50"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            helpText={`Between ${usd(TOP_UP_MIN_CENTS)} and ${usd(TOP_UP_MAX_CENTS)}.`}
            error={amountError || undefined}
          />
          {intentError && <p className="text-sm text-danger-fg">{intentError}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleContinue}
              loading={busy}
              disabled={amountCents === null || outOfBounds || busy}
            >
              Continue
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-text-muted">
            Adding{' '}
            <span className="font-medium text-text-strong">{usd(intent.amountCents)}</span> to your
            wallet.
          </p>
          {children({
            clientSecret: intent.clientSecret,
            amountCents: intent.amountCents,
            onBack: () => setIntent(null),
          })}
        </div>
      )}
    </Dialog>
  )
}
